/* AUTO-GENERATED by scripts/api/generate_webmcp_tools.py -- do not edit by hand. */

import type { OpenAPIConfig } from '../core/OpenAPI'
import { OpenAPI } from '..'
import { request as __request } from '../core/request'
import { usePageStore } from '~/stores'

const config: OpenAPIConfig = {
  ...OpenAPI,
  HEADERS: async () => {
    const pageStore = usePageStore()
    return { 'x-csrftoken': pageStore.csrftoken ?? '' }
  },
}

export type WebMcpToolDef = {
  name: string
  description: string
  inputSchema: { type: "object"; properties: Record<string, unknown>; required?: readonly string[] }
  execute: (args: Record<string, unknown>) => Promise<unknown>
}

export const autogeneratedTools: WebMcpToolDef[] = [
  {
    name: "add_task_attachment_from_url",
    description: "Attach a file from a provided URL to a task. The file will be downloaded and attached asynchronously. This operation may take a few moments to complete.",
    inputSchema: {
      "type": "object",
      "properties": {
        "id": {
          "type": "string",
          "pattern": "^[a-zA-Z0-9]{12}$",
          "description": "The task's 12-character alphanumeric Dart ID; accepts an ID, not a title or URL."
        },
        "requestBody": {
          "type": "object",
          "properties": {
            "name": {
              "type": "string",
              "description": "The name of the file to upload."
            },
            "url": {
              "type": "string",
              "format": "uri",
              "description": "The URL of the file to upload."
            }
          },
          "required": [
            "name",
            "url"
          ]
        }
      },
      "required": [
        "id",
        "requestBody"
      ]
    },
    execute: (args) => __request(config, {
      method: 'POST',
      url: '/api/v0/ai/tasks/{id}/attachments/from-url',
      path: { 'id': args["id"] },
      body: args['requestBody'],
    }),
  },
  {
    name: "add_task_comment",
    description: "Record a new comment that the user intends to add to a given task. This will save the comment in Dart for later access, search, etc.",
    inputSchema: {
      "type": "object",
      "properties": {
        "requestBody": {
          "type": "object",
          "properties": {
            "item": {
              "type": "object",
              "properties": {
                "parentId": {
                  "type": "string",
                  "pattern": "^[a-zA-Z0-9]{12}$",
                  "description": "The universal, unique ID of the parent comment, if any."
                },
                "taskId": {
                  "type": "string",
                  "pattern": "^[a-zA-Z0-9]{12}$",
                  "description": "The universal, unique ID of the task that the comment is associated with."
                },
                "text": {
                  "type": "string",
                  "description": "The full content of the comment, which can include markdown formatting."
                }
              },
              "required": [
                "taskId",
                "text"
              ],
              "description": "The target task ID and comment text, with an optional parent comment ID for a reply."
            }
          },
          "required": [
            "item"
          ]
        }
      },
      "required": [
        "requestBody"
      ]
    },
    execute: (args) => __request(config, {
      method: 'POST',
      url: '/api/v0/ai/comments',
      body: args['requestBody'],
    }),
  },
  {
    name: "add_task_time_tracking",
    description: "Record an additional time tracking entry on a task and return the updated task with refreshed time tracking.",
    inputSchema: {
      "type": "object",
      "properties": {
        "id": {
          "type": "string",
          "pattern": "^[a-zA-Z0-9]{12}$",
          "description": "The task's 12-character alphanumeric Dart ID; accepts an ID, not a title or URL."
        },
        "requestBody": {
          "type": "object",
          "properties": {
            "user": {
              "type": [
                "string",
                "null"
              ],
              "description": "The name or email of the user to attribute the tracked time to or null to use the current user."
            },
            "startedAt": {
              "type": "string",
              "description": "The start timestamp for the tracked time entry. Use an ISO 8601 date and time with Z or a UTC offset, e.g. 2026-09-09T10:00:00Z or 2026-09-09T12:00:00+02:00. A date alone or a timestamp without a timezone is invalid."
            },
            "finishedAt": {
              "type": "string",
              "description": "The end timestamp for the tracked time entry. Must be after the start time. Use an ISO 8601 date and time with Z or a UTC offset, e.g. 2026-09-09T10:00:00Z or 2026-09-09T12:00:00+02:00. A date alone or a timestamp without a timezone is invalid."
            },
            "customPropertyName": {
              "type": [
                "string",
                "null"
              ],
              "description": "The time tracking custom property name listed in config customProperties. Must be a time tracking type. If omitted, defaults to the main time tracking property."
            }
          },
          "required": [
            "finishedAt",
            "startedAt"
          ]
        }
      },
      "required": [
        "id",
        "requestBody"
      ]
    },
    execute: (args) => __request(config, {
      method: 'POST',
      url: '/api/v0/ai/tasks/{id}/time-tracking',
      path: { 'id': args["id"] },
      body: args['requestBody'],
    }),
  },
  {
    name: "create_agent",
    description: "Create a new agent in the workspace with a name and optional description or instructions.",
    inputSchema: {
      "type": "object",
      "properties": {
        "requestBody": {
          "type": "object",
          "properties": {
            "item": {
              "type": "object",
              "properties": {
                "name": {
                  "type": "string",
                  "description": "The display name of the new agent.",
                  "minLength": 1
                },
                "executionMode": {
                  "enum": [
                    "Instructions",
                    "Forwarding",
                    "Local",
                    "None"
                  ],
                  "type": "string",
                  "description": "How the agent runs when triggered.",
                  "x-enum-varnames": [
                    "INSTRUCTIONS",
                    "FORWARDING",
                    "LOCAL",
                    "NONE"
                  ]
                },
                "instructions": {
                  "type": "object",
                  "properties": {
                    "markdown": {
                      "type": "string",
                      "description": "The agent's instructions in markdown format."
                    },
                    "model": {
                      "enum": [
                        "auto",
                        "gpt-6-astra",
                        "gpt-6.1-terra",
                        "gpt-6-luna",
                        "claude-fable-5-1",
                        "claude-opus-5-5",
                        "claude-sonnet-5-5",
                        "gemini-3.1-flash-lite",
                        "gemini-3.8-flash",
                        "gemini-4-argon",
                        "grok-4.7",
                        "grok-4.20",
                        "muse-wonder-1.0",
                        "muse-spark-1.3",
                        "global.amazon.nova-2-lite-v1:0",
                        "us.amazon.nova-pro-v1:0",
                        "command-a-plus-05-2026",
                        "command-a-03-2025"
                      ],
                      "type": "string",
                      "description": "The model used by instructions agents.",
                      "x-enum-varnames": [
                        "AUTO",
                        "GPT_6_ASTRA",
                        "GPT_6_1_TERRA",
                        "GPT_6_LUNA",
                        "CLAUDE_FABLE_5_1",
                        "CLAUDE_OPUS_5_5",
                        "CLAUDE_SONNET_5_5",
                        "GEMINI_3_1_FLASH_LITE",
                        "GEMINI_3_8_FLASH",
                        "GEMINI_4_ARGON",
                        "GROK_4_7",
                        "GROK_4_20",
                        "MUSE_WONDER_1_0",
                        "MUSE_SPARK_1_3",
                        "NOVA_2_LITE",
                        "NOVA_PRO_1_0",
                        "COMMAND_A_PLUS",
                        "COMMAND_A"
                      ]
                    },
                    "thinkingLevel": {
                      "enum": [
                        "none",
                        "low",
                        "medium",
                        "high",
                        "xhigh"
                      ],
                      "type": "string",
                      "description": "The thinking level used by instructions agents.",
                      "x-enum-varnames": [
                        "NONE",
                        "LOW",
                        "MEDIUM",
                        "HIGH",
                        "XHIGH"
                      ]
                    },
                    "webEnabled": {
                      "type": "boolean",
                      "description": "Whether web access is enabled for instructions agents."
                    }
                  },
                  "description": "Instructions-mode configuration."
                },
                "forwarding": {
                  "type": "object",
                  "properties": {
                    "url": {
                      "description": "The URL to call when the agent is triggered.",
                      "oneOf": [
                        {
                          "type": "string",
                          "format": "uri"
                        },
                        {
                          "type": "string",
                          "maxLength": 0
                        }
                      ]
                    },
                    "headers": {
                      "type": "object",
                      "additionalProperties": {
                        "type": "string"
                      },
                      "description": "Headers to include with the forwarding request."
                    },
                    "body": {
                      "type": "string",
                      "description": "The JSON body template for the forwarding request."
                    },
                    "responseKey": {
                      "type": "string",
                      "description": "The JSON response key to use as the agent's comment."
                    }
                  },
                  "description": "Forwarding-mode configuration. Include url to apply this configuration; other forwarding fields are ignored without it. Providing url replaces the configuration: omitted headers become empty, and omitted body and responseKey become empty strings."
                },
                "local": {
                  "type": "object",
                  "properties": {
                    "agent": {
                      "enum": [
                        "agy",
                        "claude",
                        "codex",
                        "copilot",
                        "cursor",
                        "devin",
                        "gemini",
                        "grok",
                        "muse",
                        "opencode",
                        "prime-agent",
                        "vibe"
                      ],
                      "type": "string",
                      "description": "The local agent type used by local agents.",
                      "x-enum-varnames": [
                        "AGY",
                        "CLAUDE",
                        "CODEX",
                        "COPILOT",
                        "CURSOR",
                        "DEVIN",
                        "GEMINI",
                        "GROK",
                        "MUSE",
                        "OPENCODE",
                        "PRIME_AGENT",
                        "VIBE"
                      ]
                    }
                  },
                  "description": "Local-mode configuration."
                }
              },
              "required": [
                "name"
              ],
              "description": "The new agent's name and optional instructions and execution settings."
            }
          },
          "required": [
            "item"
          ]
        }
      },
      "required": [
        "requestBody"
      ]
    },
    execute: (args) => __request(config, {
      method: 'POST',
      url: '/api/v0/ai/agents',
      body: args['requestBody'],
    }),
  },
  {
    name: "create_doc",
    description: "Record a new doc that the user intends to write down. This will save the doc in Dart for later access, search, etc. By default the created doc will be in the Docs folder. More information can be included in the text.",
    inputSchema: {
      "type": "object",
      "properties": {
        "requestBody": {
          "type": "object",
          "properties": {
            "item": {
              "type": "object",
              "properties": {
                "title": {
                  "type": "string",
                  "description": "The title, which is a short description of the doc."
                },
                "folder": {
                  "type": "string",
                  "description": "The full title of the folder, which is a project or list of docs."
                },
                "text": {
                  "type": "string",
                  "description": "The full content of the doc, which can include markdown formatting."
                }
              },
              "required": [
                "title"
              ],
              "description": "The new doc's title and optional folder and Markdown text."
            }
          },
          "required": [
            "item"
          ]
        }
      },
      "required": [
        "requestBody"
      ]
    },
    execute: (args) => __request(config, {
      method: 'POST',
      url: '/api/v0/ai/docs',
      body: args['requestBody'],
    }),
  },
  {
    name: "create_task",
    description: "Record a new task that the user intends to do. By default the task will be assigned to the current user, have an unstarted status, have no parent, and be in the default dartboard. More information can be included in the description.",
    inputSchema: {
      "type": "object",
      "properties": {
        "requestBody": {
          "type": "object",
          "properties": {
            "item": {
              "type": "object",
              "properties": {
                "title": {
                  "type": "string",
                  "description": "The title, which is a short description of what needs to be done."
                },
                "parentId": {
                  "oneOf": [
                    {
                      "type": "string",
                      "pattern": "^[a-zA-Z0-9]{12}$",
                      "description": "The universal, unique ID of the task."
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "description": "The universal, unique ID of the parent task. This can be null. These tasks have a parent-child relationship where the current task is the child and this task ID corresponds to the parent. Subtasks inherit context from their parent and are typically smaller units of work."
                },
                "dartboard": {
                  "type": "string",
                  "description": "The full title of the dartboard, which is a project or list of tasks."
                },
                "type": {
                  "type": "string",
                  "description": "The title of the type of the task."
                },
                "status": {
                  "type": "string",
                  "description": "The status from the list of available statuses."
                },
                "description": {
                  "type": "string",
                  "description": "A longer description of the task, which can include markdown formatting."
                },
                "assignees": {
                  "type": [
                    "array",
                    "null"
                  ],
                  "items": {
                    "type": "string"
                  },
                  "description": "Names or emails replacing all assignees for a workspace allowing multiple assignees. An empty array clears them."
                },
                "assignee": {
                  "type": [
                    "string",
                    "null"
                  ],
                  "description": "The assignee's name or email for a workspace using a single assignee. Null clears the assignee."
                },
                "reviewers": {
                  "type": [
                    "array",
                    "null"
                  ],
                  "items": {
                    "type": "string"
                  },
                  "description": "Names or emails replacing all reviewers for a workspace allowing multiple reviewers. An empty array clears them."
                },
                "reviewer": {
                  "type": [
                    "string",
                    "null"
                  ],
                  "description": "The reviewer's name or email for a workspace using a single reviewer. Null clears the reviewer."
                },
                "tags": {
                  "type": "array",
                  "items": {
                    "type": "string"
                  },
                  "description": "Tag titles replacing all task tags. An empty array clears them. Unknown titles create new tags; use / for nested tags."
                },
                "priority": {
                  "oneOf": [
                    {
                      "enum": [
                        "Critical",
                        "High",
                        "Medium",
                        "Low"
                      ],
                      "type": "string",
                      "description": "* `Critical` - CRITICAL\n* `High` - HIGH\n* `Medium` - MEDIUM\n* `Low` - LOW",
                      "x-enum-varnames": [
                        "CRITICAL",
                        "HIGH",
                        "MEDIUM",
                        "LOW"
                      ]
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "description": "The priority, which is a string that can be one of the specified options. This is used to sort tasks and determine which tasks should be done first."
                },
                "startAt": {
                  "type": [
                    "string",
                    "null"
                  ],
                  "description": "The start date, which is a date that the task should be started by in ISO format, like YYYY-MM-DD."
                },
                "dueAt": {
                  "type": [
                    "string",
                    "null"
                  ],
                  "description": "The due date, which is a date that the task should be completed by in ISO format, like YYYY-MM-DD."
                },
                "size": {
                  "type": [
                    "string",
                    "integer",
                    "null"
                  ],
                  "description": "The size, which represents the amount of work that needs to be done. This is used to determine how long the task will take to complete."
                },
                "customProperties": {
                  "type": [
                    "object",
                    "null"
                  ],
                  "description": "Custom properties as a dict mapping the exact, case-sensitive name values from workspace config customProperties to values. These are generated keys, not display titles or IDs (e.g., {\"customCheckboxProperty\": true, \"customTextProperty\": \"Some text\"}).",
                  "properties": {},
                  "additionalProperties": {
                    "anyOf": [
                      {
                        "title": "CustomPropertyCheckbox",
                        "type": "boolean"
                      },
                      {
                        "title": "CustomPropertyDatesRange",
                        "type": [
                          "array",
                          "null"
                        ],
                        "items": {
                          "type": [
                            "string",
                            "null"
                          ]
                        }
                      },
                      {
                        "title": "CustomPropertyDatesSingle",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      {
                        "title": "CustomPropertyMultiselect",
                        "type": "array",
                        "items": {
                          "type": "string"
                        }
                      },
                      {
                        "title": "CustomPropertyNumberWithIntegerFormat",
                        "type": [
                          "integer",
                          "null"
                        ]
                      },
                      {
                        "title": "CustomPropertyNumberWithPercentageFormat",
                        "type": [
                          "integer",
                          "null"
                        ]
                      },
                      {
                        "title": "CustomPropertyNumberWithDollarsFormat",
                        "type": [
                          "number",
                          "null"
                        ]
                      },
                      {
                        "title": "CustomPropertySelect",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      {
                        "title": "CustomPropertyStatus",
                        "type": "string"
                      },
                      {
                        "title": "CustomPropertyText",
                        "type": "string"
                      },
                      {
                        "title": "CustomPropertyUserMultiple",
                        "type": "array",
                        "items": {
                          "type": "string"
                        }
                      },
                      {
                        "title": "CustomPropertyUserSingle",
                        "type": [
                          "string",
                          "null"
                        ]
                      }
                    ]
                  },
                  "example": {
                    "customCheckboxProperty": true,
                    "customDatesProperty": "2025-05-10",
                    "customDatesPropertyWithRange": [
                      "2025-05-01",
                      "2025-05-30"
                    ],
                    "customMultiselectProperty": [
                      "frontend",
                      "bug"
                    ],
                    "customNumberPropertyWithIntegerFormat": 5,
                    "customNumberPropertyWithPercentageFormat": 75,
                    "customNumberPropertyWithDollarsFormat": 1500.5,
                    "customSelectProperty": "In Progress",
                    "customStatusProperty": "Blocked",
                    "customTextProperty": "This task requires additional review from the design team",
                    "customUserProperty": "john.doe@example.com",
                    "customMultipleUserProperty": [
                      "john.doe@example.com",
                      "Alice Smith"
                    ]
                  }
                },
                "taskRelationships": {
                  "oneOf": [
                    {
                      "type": "object",
                      "properties": {
                        "subtaskIds": {
                          "type": "array",
                          "items": {
                            "type": "string",
                            "pattern": "^[a-zA-Z0-9]{12}$",
                            "description": "The universal, unique ID of the task."
                          },
                          "description": "Array of task IDs that are subtasks of this task. These tasks have a parent-child relationship where this task is the parent. Subtasks inherit context from their parent and are typically smaller units of work."
                        },
                        "blockerIds": {
                          "type": "array",
                          "items": {
                            "type": "string",
                            "pattern": "^[a-zA-Z0-9]{12}$",
                            "description": "The universal, unique ID of the task."
                          },
                          "description": "Array of task IDs that block this task from being completed. These are dependencies that must be resolved/completed before this task can proceed. The blocking tasks have priority over this task."
                        },
                        "blockingIds": {
                          "type": "array",
                          "items": {
                            "type": "string",
                            "pattern": "^[a-zA-Z0-9]{12}$",
                            "description": "The universal, unique ID of the task."
                          },
                          "description": "Array of task IDs that are blocked by this task. This task must be completed before the blocked tasks can proceed. This task is a dependency for the blocked tasks and has priority over them."
                        },
                        "duplicateIds": {
                          "type": "array",
                          "items": {
                            "type": "string",
                            "pattern": "^[a-zA-Z0-9]{12}$",
                            "description": "The universal, unique ID of the task."
                          },
                          "description": "Array of task IDs that are duplicates of this task. These represent the same work item and should typically be consolidated to avoid duplicate effort. Only one of the duplicates should be completed."
                        },
                        "relatedIds": {
                          "type": "array",
                          "items": {
                            "type": "string",
                            "pattern": "^[a-zA-Z0-9]{12}$",
                            "description": "The universal, unique ID of the task."
                          },
                          "description": "Array of task IDs that are related to this task. These tasks have some contextual relationship but no direct dependency. They may share similar goals, components, or be part of the same feature/epic."
                        }
                      },
                      "additionalProperties": false,
                      "nullable": true,
                      "example": {
                        "subtaskIds": [
                          "abcdefghijk1",
                          "abcdefghijk2"
                        ],
                        "blockerIds": [
                          "abcdefghijk3"
                        ],
                        "blockingIds": [
                          "abcdefghijk4"
                        ],
                        "duplicateIds": [
                          "abcdefghijk5"
                        ],
                        "relatedIds": [
                          "abcdefghijk6",
                          "abcdefghijk7"
                        ]
                      }
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "description": "The relationships associated with the task."
                }
              },
              "required": [
                "title"
              ],
              "description": "The new task's title and optional properties. Use workspace config for workspace-specific property values."
            }
          },
          "required": [
            "item"
          ]
        }
      },
      "required": [
        "requestBody"
      ]
    },
    execute: (args) => __request(config, {
      method: 'POST',
      url: '/api/v0/ai/tasks',
      body: args['requestBody'],
    }),
  },
  {
    name: "delete_agent",
    description: "Delete an agent by its ID.",
    inputSchema: {
      "type": "object",
      "properties": {
        "id": {
          "type": "string",
          "pattern": "^[a-zA-Z0-9]{12}$",
          "description": "The agent's 12-character alphanumeric Dart ID; accepts an ID, not a title or URL. Use an internal agent ID returned by the agent listing."
        }
      },
      "required": [
        "id"
      ]
    },
    execute: (args) => __request(config, {
      method: 'DELETE',
      url: '/api/v0/ai/agents/{id}',
      path: { 'id': args["id"] },
    }),
  },
  {
    name: "delete_doc",
    description: "Move an existing doc to the trash, where it can be recovered if needed. Nothing else about the doc will be changed.",
    inputSchema: {
      "type": "object",
      "properties": {
        "id": {
          "type": "string",
          "pattern": "^[a-zA-Z0-9]{12}$",
          "description": "The doc's 12-character alphanumeric Dart ID; accepts an ID, not a title or URL."
        }
      },
      "required": [
        "id"
      ]
    },
    execute: (args) => __request(config, {
      method: 'DELETE',
      url: '/api/v0/ai/docs/{id}',
      path: { 'id': args["id"] },
    }),
  },
  {
    name: "delete_task",
    description: "Move an existing task to the trash, where it can be recovered if needed. Nothing else about the task will be changed.",
    inputSchema: {
      "type": "object",
      "properties": {
        "id": {
          "type": "string",
          "pattern": "^[a-zA-Z0-9]{12}$",
          "description": "The task's 12-character alphanumeric Dart ID; accepts an ID, not a title or URL."
        }
      },
      "required": [
        "id"
      ]
    },
    execute: (args) => __request(config, {
      method: 'DELETE',
      url: '/api/v0/ai/tasks/{id}',
      path: { 'id': args["id"] },
    }),
  },
  {
    name: "get_agent",
    description: "Retrieve an existing agent by its ID, including its name and current description or instructions.",
    inputSchema: {
      "type": "object",
      "properties": {
        "id": {
          "type": "string",
          "pattern": "^[a-zA-Z0-9]{12}$",
          "description": "The agent's 12-character alphanumeric Dart ID; accepts an ID, not a title or URL. Use an internal agent ID returned by the agent listing."
        }
      },
      "required": [
        "id"
      ]
    },
    execute: (args) => __request(config, {
      method: 'GET',
      url: '/api/v0/ai/agents/{id}',
      path: { 'id': args["id"] },
    }),
  },
  {
    name: "get_config",
    description: "Get information about the user's space, including all of the possible values that can be provided to other endpoints.",
    inputSchema: {
      "type": "object",
      "properties": {}
    },
    execute: (args) => __request(config, {
      method: 'GET',
      url: '/api/v0/ai/config',
    }),
  },
  {
    name: "get_dartboard",
    description: "Retrieve an existing dartboard. This will return the dartboard's information, including the title and description.",
    inputSchema: {
      "type": "object",
      "properties": {
        "id": {
          "type": "string",
          "pattern": "^[a-zA-Z0-9]{12}$",
          "description": "The dartboard's 12-character alphanumeric Dart ID; accepts an ID, not a title or URL."
        }
      },
      "required": [
        "id"
      ]
    },
    execute: (args) => __request(config, {
      method: 'GET',
      url: '/api/v0/ai/dartboards/{id}',
      path: { 'id': args["id"] },
    }),
  },
  {
    name: "get_doc",
    description: "Retrieve an existing doc. This will return the doc's information, including the title, folder, text and others.",
    inputSchema: {
      "type": "object",
      "properties": {
        "id": {
          "type": "string",
          "pattern": "^[a-zA-Z0-9]{12}$",
          "description": "The doc's 12-character alphanumeric Dart ID; accepts an ID, not a title or URL."
        }
      },
      "required": [
        "id"
      ]
    },
    execute: (args) => __request(config, {
      method: 'GET',
      url: '/api/v0/ai/docs/{id}',
      path: { 'id': args["id"] },
    }),
  },
  {
    name: "get_folder",
    description: "Retrieve an existing folder. This will return the folder's information, including the title, description, all docs within it, and others.",
    inputSchema: {
      "type": "object",
      "properties": {
        "id": {
          "type": "string",
          "pattern": "^[a-zA-Z0-9]{12}$",
          "description": "The folder's 12-character alphanumeric Dart ID; accepts an ID, not a title or URL."
        }
      },
      "required": [
        "id"
      ]
    },
    execute: (args) => __request(config, {
      method: 'GET',
      url: '/api/v0/ai/folders/{id}',
      path: { 'id': args["id"] },
    }),
  },
  {
    name: "get_task",
    description: "Retrieve an existing task. This will return the task's information, including the title, dartboard, status, description and others.",
    inputSchema: {
      "type": "object",
      "properties": {
        "id": {
          "type": "string",
          "pattern": "^[a-zA-Z0-9]{12}$",
          "description": "The task's 12-character alphanumeric Dart ID; accepts an ID, not a title or URL."
        }
      },
      "required": [
        "id"
      ]
    },
    execute: (args) => __request(config, {
      method: 'GET',
      url: '/api/v0/ai/tasks/{id}',
      path: { 'id': args["id"] },
    }),
  },
  {
    name: "get_view",
    description: "Retrieve an existing view. This will return the view's information, including the title, description, and others.",
    inputSchema: {
      "type": "object",
      "properties": {
        "id": {
          "type": "string",
          "pattern": "^[a-zA-Z0-9]{12}$",
          "description": "The view's 12-character alphanumeric Dart ID; accepts an ID, not a title or URL."
        }
      },
      "required": [
        "id"
      ]
    },
    execute: (args) => __request(config, {
      method: 'GET',
      url: '/api/v0/ai/views/{id}',
      path: { 'id': args["id"] },
    }),
  },
  {
    name: "list_agents",
    description: "List all agents in the workspace. Agents are AI assistants that can be assigned tasks and have customizable instructions.",
    inputSchema: {
      "type": "object",
      "properties": {
        "limit": {
          "type": "integer",
          "description": "Number of results to return per page."
        },
        "offset": {
          "type": "integer",
          "description": "The initial index from which to return the results."
        }
      }
    },
    execute: (args) => __request(config, {
      method: 'GET',
      url: '/api/v0/ai/agents/list',
      query: { 'limit': args["limit"], 'offset': args["offset"] },
    }),
  },
  {
    name: "list_comments",
    description: "List comments for a task with filtering options. Filter by author, text content, or date range. Sort by date or hierarchical thread order. Task ID required. Supports pagination.",
    inputSchema: {
      "type": "object",
      "properties": {
        "author": {
          "type": "string",
          "description": "Case-insensitive substring of the comment author's name or email."
        },
        "author_id": {
          "type": "string",
          "description": "The Dart user ID of the comment author; accepts an ID, not a name or email."
        },
        "ids": {
          "type": "string",
          "description": "Comma-separated Dart IDs to include. Each ID is a 12-character alphanumeric string."
        },
        "limit": {
          "type": "integer",
          "description": "Number of results to return per page."
        },
        "o": {
          "type": "array",
          "items": {
            "type": "string",
            "enum": [
              "-published_at",
              "hierarchical",
              "published_at"
            ]
          },
          "description": "Ordering\n\n* `published_at` - Published At\n* `-published_at` - Published At (descending)\n* `hierarchical` - Hierarchical"
        },
        "offset": {
          "type": "integer",
          "description": "The initial index from which to return the results."
        },
        "parent_id": {
          "type": "string",
          "description": "The Dart ID of the parent comment whose replies to return. An empty string matches top-level comments."
        },
        "published_at": {
          "type": "string",
          "format": "date-time",
          "description": "Exact comment publication time to match. Use an ISO 8601 timestamp, preferably with Z or a UTC offset, e.g. 2026-09-09T12:00:00Z."
        },
        "published_at_after": {
          "type": "string",
          "format": "date-time",
          "description": "Inclusive lower bound on comment publication time. Use an ISO 8601 timestamp, preferably with Z or a UTC offset, e.g. 2026-09-09T12:00:00Z."
        },
        "published_at_before": {
          "type": "string",
          "format": "date-time",
          "description": "Inclusive upper bound on comment publication time. Use an ISO 8601 timestamp, preferably with Z or a UTC offset, e.g. 2026-09-09T12:00:00Z."
        },
        "task": {
          "type": "string",
          "description": "Case-insensitive substring of the commented task's title."
        },
        "task_id": {
          "type": "string",
          "description": "The Dart ID of the task whose comments to list; required even when task is provided."
        },
        "text": {
          "type": "string",
          "description": "Case-insensitive substring of the stored rich-text content, including formatting metadata."
        }
      },
      "required": [
        "task_id"
      ]
    },
    execute: (args) => __request(config, {
      method: 'GET',
      url: '/api/v0/ai/comments/list',
      query: { 'author': args["author"], 'author_id': args["author_id"], 'ids': args["ids"], 'limit': args["limit"], 'o': args["o"], 'offset': args["offset"], 'parent_id': args["parent_id"], 'published_at': args["published_at"], 'published_at_after': args["published_at_after"], 'published_at_before': args["published_at_before"], 'task': args["task"], 'task_id': args["task_id"], 'text': args["text"] },
    }),
  },
  {
    name: "list_docs",
    description: "List docs with filtering and search capabilities. Filter by folder, title, text content, or use full-text search. Sort by creation/update date or title. Supports pagination.",
    inputSchema: {
      "type": "object",
      "properties": {
        "editor": {
          "type": "string",
          "description": "Case-insensitive substring of the editor's name or email. An empty string matches items with no editor."
        },
        "folder": {
          "type": "string",
          "description": "Case-insensitive substring of the folder title, optionally prefixed with Space/ to also match the space title."
        },
        "folder_id": {
          "type": "string",
          "description": "The Dart ID of the folder to filter by; accepts an ID, not a title."
        },
        "ids": {
          "type": "string",
          "description": "Comma-separated Dart IDs to include. Each ID is a 12-character alphanumeric string."
        },
        "in_trash": {
          "type": "boolean",
          "description": "Whether to return trashed items instead of non-trashed items. Defaults to false, even with no_defaults=true."
        },
        "limit": {
          "type": "integer",
          "description": "Number of results to return per page."
        },
        "no_defaults": {
          "type": "boolean",
          "default": true,
          "description": "Whether to skip workspace-specific default filters. Defaults to true. Default ordering and the exclusion of trashed items still apply; use o and in_trash to override them."
        },
        "o": {
          "type": "array",
          "items": {
            "type": "string",
            "enum": [
              "-created_at",
              "-folder__order",
              "-order",
              "-title",
              "-updated_at",
              "created_at",
              "folder__order",
              "order",
              "title",
              "updated_at"
            ]
          },
          "description": "Ordering\n\n* `folder__order` - Folder order\n* `-folder__order` - Folder order (desc)\n* `order` - Order\n* `-order` - Order (desc)\n* `created_at` - Created\n* `-created_at` - Created (desc)\n* `updated_at` - Updated\n* `-updated_at` - Updated (desc)\n* `title` - Title\n* `-title` - Title (desc)"
        },
        "offset": {
          "type": "integer",
          "description": "The initial index from which to return the results."
        },
        "s": {
          "type": "string",
          "description": "Search by title, text, or folder title"
        },
        "text": {
          "type": "string",
          "description": "Case-insensitive substring of the stored rich-text content, including formatting metadata."
        },
        "title": {
          "type": "string",
          "description": "Case-insensitive substring of the title."
        }
      }
    },
    execute: (args) => __request(config, {
      method: 'GET',
      url: '/api/v0/ai/docs/list',
      query: { 'editor': args["editor"], 'folder': args["folder"], 'folder_id': args["folder_id"], 'ids': args["ids"], 'in_trash': args["in_trash"], 'limit': args["limit"], 'no_defaults': args["no_defaults"], 'o': args["o"], 'offset': args["offset"], 's': args["s"], 'text': args["text"], 'title': args["title"] },
    }),
  },
  {
    name: "list_help_center_articles",
    description: "Search for up to two help center articles by their semantic similarity to a short (1-5 word) query.",
    inputSchema: {
      "type": "object",
      "properties": {
        "query": {
          "type": "string",
          "minLength": 1,
          "description": "The search query (1-5 words) to find relevant help articles"
        }
      }
    },
    execute: (args) => __request(config, {
      method: 'GET',
      url: '/api/v0/ai/help-center-articles/list',
      query: { 'query': args["query"] },
    }),
  },
  {
    name: "list_tasks",
    description: "List tasks with powerful filtering options. Filter by dartboard, status, assignee, tags, priority, dates, completion state, view, and more. Supports pagination with limit/offset.",
    inputSchema: {
      "type": "object",
      "properties": {
        "assignee": {
          "type": "string",
          "description": "Case-insensitive substring of the assignee's name or email. An empty string matches items with no assignee."
        },
        "assignee_id": {
          "type": "string",
          "description": "The Dart user ID of the assignee; accepts an ID, not a name or email. An empty string matches items with no assignee."
        },
        "completed_at": {
          "type": "string",
          "format": "date-time",
          "description": "Exact completion time to match. Use an ISO 8601 timestamp, preferably with Z or a UTC offset, e.g. 2026-09-09T12:00:00Z."
        },
        "completed_at_after": {
          "type": "string",
          "format": "date-time",
          "description": "Inclusive lower bound on completion time. Use an ISO 8601 timestamp, preferably with Z or a UTC offset, e.g. 2026-09-09T12:00:00Z."
        },
        "completed_at_before": {
          "type": "string",
          "format": "date-time",
          "description": "Inclusive upper bound on completion time. Use an ISO 8601 timestamp, preferably with Z or a UTC offset, e.g. 2026-09-09T12:00:00Z."
        },
        "created_at": {
          "type": "string",
          "format": "date-time",
          "description": "Exact creation time to match. Use an ISO 8601 timestamp, preferably with Z or a UTC offset, e.g. 2026-09-09T12:00:00Z."
        },
        "created_at_after": {
          "type": "string",
          "format": "date-time",
          "description": "Inclusive lower bound on creation time. Use an ISO 8601 timestamp, preferably with Z or a UTC offset, e.g. 2026-09-09T12:00:00Z."
        },
        "created_at_before": {
          "type": "string",
          "format": "date-time",
          "description": "Inclusive upper bound on creation time. Use an ISO 8601 timestamp, preferably with Z or a UTC offset, e.g. 2026-09-09T12:00:00Z."
        },
        "created_by": {
          "type": "string",
          "description": "Case-insensitive substring of the creator's name or email."
        },
        "created_by_id": {
          "type": "string",
          "description": "The Dart user ID of the creator; accepts an ID, not a name or email. An empty string matches items with no creator."
        },
        "dartboard": {
          "type": "string",
          "description": "Dartboard title or full Space/Dartboard title, matched exactly ignoring case. Use the configuration response for available values."
        },
        "dartboard_id": {
          "type": "string",
          "description": "The Dart ID of the dartboard to filter by; accepts an ID, not a title."
        },
        "description": {
          "type": "string",
          "description": "Case-insensitive substring of the stored task description, including rich-text formatting metadata."
        },
        "due_at": {
          "type": "string",
          "description": "Exact due calendar date to match. Use YYYY-MM-DD, e.g. 2026-09-09. If a timestamp includes Z or an offset, its date is taken in America/Los_Angeles and may differ."
        },
        "due_at_after": {
          "type": "string",
          "description": "Inclusive lower bound on due calendar date. Use YYYY-MM-DD, e.g. 2026-09-09. If a timestamp includes Z or an offset, its date is taken in America/Los_Angeles and may differ."
        },
        "due_at_before": {
          "type": "string",
          "description": "Inclusive upper bound on due calendar date. Use YYYY-MM-DD, e.g. 2026-09-09. If a timestamp includes Z or an offset, its date is taken in America/Los_Angeles and may differ."
        },
        "ids": {
          "type": "string",
          "description": "Comma-separated Dart IDs to include. Each ID is a 12-character alphanumeric string."
        },
        "in_trash": {
          "type": "boolean",
          "description": "Whether to return trashed items instead of non-trashed items. Defaults to false, even with no_defaults=true."
        },
        "is_completed": {
          "type": "boolean",
          "description": "Whether to return tasks with a completed status; false returns tasks without one."
        },
        "limit": {
          "type": "integer",
          "description": "Number of results to return per page."
        },
        "no_defaults": {
          "type": "boolean",
          "default": true,
          "description": "Whether to skip workspace-specific default filters. Defaults to true. Default ordering and the exclusion of trashed items still apply; use o and in_trash to override them."
        },
        "o": {
          "type": "array",
          "items": {
            "type": "string",
            "enum": [
              "-completed_at",
              "-created_at",
              "-dartboard__order",
              "-order",
              "-title",
              "-updated_at",
              "completed_at",
              "created_at",
              "dartboard__order",
              "order",
              "title",
              "updated_at"
            ]
          },
          "description": "Ordering\n\n* `dartboard__order` - Dartboard order\n* `-dartboard__order` - Dartboard order (desc)\n* `order` - Order\n* `-order` - Order (desc)\n* `created_at` - Created\n* `-created_at` - Created (desc)\n* `updated_at` - Updated\n* `-updated_at` - Updated (desc)\n* `completed_at` - Completed\n* `-completed_at` - Completed (desc)\n* `title` - Title\n* `-title` - Title (desc)"
        },
        "offset": {
          "type": "integer",
          "description": "The initial index from which to return the results."
        },
        "parent_id": {
          "type": "string",
          "description": "The Dart ID of the parent task whose direct subtasks to return. An empty string matches tasks with no parent."
        },
        "priority": {
          "type": "string",
          "description": "Case-insensitive substring of a priority (Critical, High, Medium, or Low). An empty string matches tasks with no priority."
        },
        "reviewer": {
          "type": "string",
          "description": "Case-insensitive substring of the reviewer's name or email. An empty string matches items with no reviewer."
        },
        "reviewer_id": {
          "type": "string",
          "description": "The Dart user ID of the reviewer; accepts an ID, not a name or email. An empty string matches items with no reviewer."
        },
        "size": {
          "type": "integer",
          "description": "Exact numeric task size, including the stored numeric value for T-shirt sizes."
        },
        "start_at": {
          "type": "string",
          "description": "Exact start calendar date to match. Use YYYY-MM-DD, e.g. 2026-09-09. If a timestamp includes Z or an offset, its date is taken in America/Los_Angeles and may differ."
        },
        "start_at_after": {
          "type": "string",
          "description": "Inclusive lower bound on start calendar date. Use YYYY-MM-DD, e.g. 2026-09-09. If a timestamp includes Z or an offset, its date is taken in America/Los_Angeles and may differ."
        },
        "start_at_before": {
          "type": "string",
          "description": "Inclusive upper bound on start calendar date. Use YYYY-MM-DD, e.g. 2026-09-09. If a timestamp includes Z or an offset, its date is taken in America/Los_Angeles and may differ."
        },
        "status": {
          "type": "string",
          "description": "Case-insensitive substring of the task status title. Use the configuration response for available statuses."
        },
        "status_id": {
          "type": "string",
          "description": "The Dart ID of the task status to filter by; accepts an ID, not a title."
        },
        "tag": {
          "type": "string",
          "description": "Case-insensitive substring of a tag title. An empty string matches tasks with no tags."
        },
        "tag_id": {
          "type": "string",
          "description": "The Dart ID of a tag. An empty string matches tasks with no tags."
        },
        "title": {
          "type": "string",
          "description": "Case-insensitive substring of the title."
        },
        "type": {
          "type": "string",
          "description": "Case-insensitive substring of the task type title. Use the configuration response for available types."
        },
        "type_id": {
          "type": "string",
          "description": "The Dart ID of the task type to filter by; accepts an ID, not a title."
        },
        "updated_at": {
          "type": "string",
          "format": "date-time",
          "description": "Exact last update time to match. Use an ISO 8601 timestamp, preferably with Z or a UTC offset, e.g. 2026-09-09T12:00:00Z."
        },
        "updated_at_after": {
          "type": "string",
          "format": "date-time",
          "description": "Inclusive lower bound on last update time. Use an ISO 8601 timestamp, preferably with Z or a UTC offset, e.g. 2026-09-09T12:00:00Z."
        },
        "updated_at_before": {
          "type": "string",
          "format": "date-time",
          "description": "Inclusive upper bound on last update time. Use an ISO 8601 timestamp, preferably with Z or a UTC offset, e.g. 2026-09-09T12:00:00Z."
        },
        "updated_by": {
          "type": "string",
          "description": "Case-insensitive substring of the last updater's name or email."
        },
        "updated_by_id": {
          "type": "string",
          "description": "The Dart user ID of the last updater; accepts an ID, not a name or email. An empty string matches items with no last updater."
        },
        "view": {
          "type": "string",
          "description": "Case-insensitive substring of a view title. Uses the first matching view; use the view ID filter for an unambiguous selection."
        },
        "view_id": {
          "type": "string",
          "description": "The Dart ID of the view whose tasks to return; accepts an ID, not a title."
        }
      }
    },
    execute: (args) => __request(config, {
      method: 'GET',
      url: '/api/v0/ai/tasks/list',
      query: { 'assignee': args["assignee"], 'assignee_id': args["assignee_id"], 'completed_at': args["completed_at"], 'completed_at_after': args["completed_at_after"], 'completed_at_before': args["completed_at_before"], 'created_at': args["created_at"], 'created_at_after': args["created_at_after"], 'created_at_before': args["created_at_before"], 'created_by': args["created_by"], 'created_by_id': args["created_by_id"], 'dartboard': args["dartboard"], 'dartboard_id': args["dartboard_id"], 'description': args["description"], 'due_at': args["due_at"], 'due_at_after': args["due_at_after"], 'due_at_before': args["due_at_before"], 'ids': args["ids"], 'in_trash': args["in_trash"], 'is_completed': args["is_completed"], 'limit': args["limit"], 'no_defaults': args["no_defaults"], 'o': args["o"], 'offset': args["offset"], 'parent_id': args["parent_id"], 'priority': args["priority"], 'reviewer': args["reviewer"], 'reviewer_id': args["reviewer_id"], 'size': args["size"], 'start_at': args["start_at"], 'start_at_after': args["start_at_after"], 'start_at_before': args["start_at_before"], 'status': args["status"], 'status_id': args["status_id"], 'tag': args["tag"], 'tag_id': args["tag_id"], 'title': args["title"], 'type': args["type"], 'type_id': args["type_id"], 'updated_at': args["updated_at"], 'updated_at_after': args["updated_at_after"], 'updated_at_before': args["updated_at_before"], 'updated_by': args["updated_by"], 'updated_by_id': args["updated_by_id"], 'view': args["view"], 'view_id': args["view_id"] },
    }),
  },
  {
    name: "move_task",
    description: "Move a task to a specific position within results sorted by the order field. Use afterTaskId to place the task after a specific task, or beforeTaskId to place it before one.",
    inputSchema: {
      "type": "object",
      "properties": {
        "id": {
          "type": "string",
          "pattern": "^[a-zA-Z0-9]{12}$",
          "description": "The task's 12-character alphanumeric Dart ID; accepts an ID, not a title or URL."
        },
        "requestBody": {
          "type": "object",
          "properties": {
            "beforeTaskId": {
              "oneOf": [
                {
                  "type": "string",
                  "pattern": "^[a-zA-Z0-9]{12}$",
                  "description": "The universal, unique ID of the task."
                },
                {
                  "type": "null"
                }
              ],
              "description": "Move the task immediately before this task. For example: if tasks are [A, B, C], then beforeTaskId=B produces [A, moved_task, B, C]. Use null to move to the beginning (first position)."
            },
            "afterTaskId": {
              "oneOf": [
                {
                  "type": "string",
                  "pattern": "^[a-zA-Z0-9]{12}$",
                  "description": "The universal, unique ID of the task."
                },
                {
                  "type": "null"
                }
              ],
              "description": "Move the task immediately after this task. For example: if tasks are [A, B, C], then afterTaskId=B produces [A, B, moved_task, C]. Use null to move to the end (last position)."
            }
          }
        }
      },
      "required": [
        "id"
      ]
    },
    execute: (args) => __request(config, {
      method: 'POST',
      url: '/api/v0/ai/tasks/{id}/move',
      path: { 'id': args["id"] },
      body: args['requestBody'],
    }),
  },
  {
    name: "report_issue",
    description: "Create a concise markdown issue report for Dart Support. Include what the user was trying to do, what failed, affected task/doc/chat names or links when available, exact error text if any, and the steps already tried. Do not include hidden reasoning, credentials, or unnecessary internal IDs.",
    inputSchema: {
      "type": "object",
      "properties": {
        "requestBody": {
          "type": "object",
          "properties": {
            "item": {
              "type": "object",
              "properties": {
                "text": {
                  "type": "string",
                  "description": "The full issue report in markdown format."
                }
              },
              "required": [
                "text"
              ],
              "description": "The full issue report in Markdown to submit to Dart support."
            }
          },
          "required": [
            "item"
          ]
        }
      },
      "required": [
        "requestBody"
      ]
    },
    execute: (args) => __request(config, {
      method: 'POST',
      url: '/api/v0/ai/issue-reports',
      body: args['requestBody'],
    }),
  },
  {
    name: "retrieve_skill_by_title",
    description: "Retrieve a skill by its title. Skills are user-defined instructions or templates for performing specific task types in the workspace. Returns the skill's title and instructions if found.",
    inputSchema: {
      "type": "object",
      "properties": {
        "title": {
          "type": "string",
          "minLength": 1,
          "description": "The title of the skill to retrieve"
        }
      },
      "required": [
        "title"
      ]
    },
    execute: (args) => __request(config, {
      method: 'GET',
      url: '/api/v0/ai/skills/by-title',
      query: { 'title': args["title"] },
    }),
  },
  {
    name: "update_agent",
    description: "Update an existing agent. The agent ID and fields to change. Use the same ID as the path id; omitted top-level fields keep their current values.",
    inputSchema: {
      "type": "object",
      "properties": {
        "id": {
          "type": "string",
          "pattern": "^[a-zA-Z0-9]{12}$",
          "description": "The agent's 12-character alphanumeric Dart ID; accepts an ID, not a title or URL. Use an internal agent ID returned by the agent listing."
        },
        "requestBody": {
          "type": "object",
          "properties": {
            "item": {
              "type": "object",
              "properties": {
                "id": {
                  "type": "string",
                  "pattern": "^[a-zA-Z0-9]{12}$",
                  "description": "The unique ID of the agent to update."
                },
                "name": {
                  "type": "string",
                  "description": "The new display name for the agent.",
                  "minLength": 1
                },
                "executionMode": {
                  "enum": [
                    "Instructions",
                    "Forwarding",
                    "Local",
                    "None"
                  ],
                  "type": "string",
                  "description": "How the agent runs when triggered.",
                  "x-enum-varnames": [
                    "INSTRUCTIONS",
                    "FORWARDING",
                    "LOCAL",
                    "NONE"
                  ]
                },
                "instructions": {
                  "type": "object",
                  "properties": {
                    "markdown": {
                      "type": "string",
                      "description": "The agent's instructions in markdown format."
                    },
                    "model": {
                      "enum": [
                        "auto",
                        "gpt-6-astra",
                        "gpt-6.1-terra",
                        "gpt-6-luna",
                        "claude-fable-5-1",
                        "claude-opus-5-5",
                        "claude-sonnet-5-5",
                        "gemini-3.1-flash-lite",
                        "gemini-3.8-flash",
                        "gemini-4-argon",
                        "grok-4.7",
                        "grok-4.20",
                        "muse-wonder-1.0",
                        "muse-spark-1.3",
                        "global.amazon.nova-2-lite-v1:0",
                        "us.amazon.nova-pro-v1:0",
                        "command-a-plus-05-2026",
                        "command-a-03-2025"
                      ],
                      "type": "string",
                      "description": "The model used by instructions agents.",
                      "x-enum-varnames": [
                        "AUTO",
                        "GPT_6_ASTRA",
                        "GPT_6_1_TERRA",
                        "GPT_6_LUNA",
                        "CLAUDE_FABLE_5_1",
                        "CLAUDE_OPUS_5_5",
                        "CLAUDE_SONNET_5_5",
                        "GEMINI_3_1_FLASH_LITE",
                        "GEMINI_3_8_FLASH",
                        "GEMINI_4_ARGON",
                        "GROK_4_7",
                        "GROK_4_20",
                        "MUSE_WONDER_1_0",
                        "MUSE_SPARK_1_3",
                        "NOVA_2_LITE",
                        "NOVA_PRO_1_0",
                        "COMMAND_A_PLUS",
                        "COMMAND_A"
                      ]
                    },
                    "thinkingLevel": {
                      "enum": [
                        "none",
                        "low",
                        "medium",
                        "high",
                        "xhigh"
                      ],
                      "type": "string",
                      "description": "The thinking level used by instructions agents.",
                      "x-enum-varnames": [
                        "NONE",
                        "LOW",
                        "MEDIUM",
                        "HIGH",
                        "XHIGH"
                      ]
                    },
                    "webEnabled": {
                      "type": "boolean",
                      "description": "Whether web access is enabled for instructions agents."
                    }
                  },
                  "description": "Instructions-mode configuration."
                },
                "forwarding": {
                  "type": "object",
                  "properties": {
                    "url": {
                      "description": "The URL to call when the agent is triggered.",
                      "oneOf": [
                        {
                          "type": "string",
                          "format": "uri"
                        },
                        {
                          "type": "string",
                          "maxLength": 0
                        }
                      ]
                    },
                    "headers": {
                      "type": "object",
                      "additionalProperties": {
                        "type": "string"
                      },
                      "description": "Headers to include with the forwarding request."
                    },
                    "body": {
                      "type": "string",
                      "description": "The JSON body template for the forwarding request."
                    },
                    "responseKey": {
                      "type": "string",
                      "description": "The JSON response key to use as the agent's comment."
                    }
                  },
                  "description": "Forwarding-mode configuration. Include url to apply this configuration; other forwarding fields are ignored without it. Providing url replaces the configuration: omitted headers become empty, and omitted body and responseKey become empty strings."
                },
                "local": {
                  "type": "object",
                  "properties": {
                    "agent": {
                      "enum": [
                        "agy",
                        "claude",
                        "codex",
                        "copilot",
                        "cursor",
                        "devin",
                        "gemini",
                        "grok",
                        "muse",
                        "opencode",
                        "prime-agent",
                        "vibe"
                      ],
                      "type": "string",
                      "description": "The local agent type used by local agents.",
                      "x-enum-varnames": [
                        "AGY",
                        "CLAUDE",
                        "CODEX",
                        "COPILOT",
                        "CURSOR",
                        "DEVIN",
                        "GEMINI",
                        "GROK",
                        "MUSE",
                        "OPENCODE",
                        "PRIME_AGENT",
                        "VIBE"
                      ]
                    }
                  },
                  "description": "Local-mode configuration."
                }
              },
              "required": [
                "id"
              ],
              "description": "The agent ID and fields to change. Use the same ID as the path id; omitted top-level fields keep their current values."
            }
          },
          "required": [
            "item"
          ]
        }
      },
      "required": [
        "id",
        "requestBody"
      ]
    },
    execute: (args) => __request(config, {
      method: 'PUT',
      url: '/api/v0/ai/agents/{id}',
      path: { 'id': args["id"] },
      body: args['requestBody'],
    }),
  },
  {
    name: "update_doc",
    description: "Update certain properties of an existing doc. This will save the doc in Dart for later access, search, etc. Any properties that are not specified will not be changed.",
    inputSchema: {
      "type": "object",
      "properties": {
        "id": {
          "type": "string",
          "pattern": "^[a-zA-Z0-9]{12}$",
          "description": "The doc's 12-character alphanumeric Dart ID; accepts an ID, not a title or URL."
        },
        "requestBody": {
          "type": "object",
          "properties": {
            "item": {
              "type": "object",
              "properties": {
                "id": {
                  "type": "string",
                  "pattern": "^[a-zA-Z0-9]{12}$",
                  "description": "The universal, unique ID of the doc."
                },
                "title": {
                  "type": "string",
                  "description": "The title, which is a short description of the doc."
                },
                "folder": {
                  "type": "string",
                  "description": "The full title of the folder, which is a project or list of docs."
                },
                "text": {
                  "type": "string",
                  "description": "The full content of the doc, which can include markdown formatting."
                }
              },
              "required": [
                "id"
              ],
              "description": "The doc ID and fields to change. Use the same ID as the path id; omitted fields keep their current values."
            }
          },
          "required": [
            "item"
          ]
        }
      },
      "required": [
        "id",
        "requestBody"
      ]
    },
    execute: (args) => __request(config, {
      method: 'PUT',
      url: '/api/v0/ai/docs/{id}',
      path: { 'id': args["id"] },
      body: args['requestBody'],
    }),
  },
  {
    name: "update_doc_text",
    description: "Apply targeted text updates to a doc's content; use instead of updateDoc when only the text body changes. Each update is one of: \"replace\" (swap oldText for newText), \"insert_before\" / \"insert_after\" (insert newText relative to anchorText), or \"delete\" (remove oldText), applied in order and atomically. When occurrence is omitted, the target text must be unique; otherwise specify occurrence (1-indexed). Preferred over a full update for long content: fewer tokens, and no risk of rewriting unrelated text.",
    inputSchema: {
      "type": "object",
      "properties": {
        "id": {
          "type": "string",
          "pattern": "^[a-zA-Z0-9]{12}$",
          "description": "The doc's 12-character alphanumeric Dart ID; accepts an ID, not a title or URL."
        },
        "requestBody": {
          "type": "object",
          "description": "Payload for applying a list of targeted text updates to a doc's text content.",
          "properties": {
            "updates": {
              "type": "array",
              "items": {
                "type": "object",
                "properties": {
                  "type": {
                    "enum": [
                      "replace",
                      "insert_before",
                      "insert_after",
                      "delete"
                    ],
                    "type": "string",
                    "description": "The kind of update. \"replace\" swaps oldText for newText. \"insert_before\" and \"insert_after\" insert newText immediately before or after anchorText. \"delete\" removes oldText.",
                    "x-enum-varnames": [
                      "REPLACE",
                      "INSERT_BEFORE",
                      "INSERT_AFTER",
                      "DELETE"
                    ]
                  },
                  "oldText": {
                    "type": "string",
                    "description": "The exact text to find. Required for \"replace\" and \"delete\"."
                  },
                  "anchorText": {
                    "type": "string",
                    "description": "The exact text to find as an insertion anchor. Required for \"insert_before\" and \"insert_after\"."
                  },
                  "newText": {
                    "type": "string",
                    "description": "The text to insert. Required for \"replace\", \"insert_before\", and \"insert_after\"."
                  },
                  "occurrence": {
                    "type": "integer",
                    "minimum": 1,
                    "description": "Which occurrence of oldText or anchorText to act on, 1-indexed. When omitted, the search text must be unique in the current content."
                  }
                },
                "required": [
                  "type"
                ]
              },
              "description": "An ordered list of text updates to apply. Each one operates on the result of the previous one. Applied atomically \u2014 if any update fails, none are persisted.",
              "minItems": 1
            }
          },
          "required": [
            "updates"
          ]
        }
      },
      "required": [
        "id",
        "requestBody"
      ]
    },
    execute: (args) => __request(config, {
      method: 'POST',
      url: '/api/v0/ai/docs/{id}/update-text',
      path: { 'id': args["id"] },
      body: args['requestBody'],
    }),
  },
  {
    name: "update_task",
    description: "Update properties of an existing task. For customProperties, use property NAME (not ID) as key. Get property names from workspace config. Example: {\"customTextProperty\": \"Some text\"}. Fields not provided are unchanged.",
    inputSchema: {
      "type": "object",
      "properties": {
        "id": {
          "type": "string",
          "pattern": "^[a-zA-Z0-9]{12}$",
          "description": "The task's 12-character alphanumeric Dart ID; accepts an ID, not a title or URL."
        },
        "requestBody": {
          "type": "object",
          "properties": {
            "item": {
              "type": "object",
              "properties": {
                "id": {
                  "type": "string",
                  "pattern": "^[a-zA-Z0-9]{12}$",
                  "description": "The universal, unique ID of the task."
                },
                "title": {
                  "type": "string",
                  "description": "The title, which is a short description of what needs to be done."
                },
                "parentId": {
                  "oneOf": [
                    {
                      "type": "string",
                      "pattern": "^[a-zA-Z0-9]{12}$",
                      "description": "The universal, unique ID of the task."
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "description": "The universal, unique ID of the parent task. This can be null. These tasks have a parent-child relationship where the current task is the child and this task ID corresponds to the parent. Subtasks inherit context from their parent and are typically smaller units of work."
                },
                "dartboard": {
                  "type": "string",
                  "description": "The full title of the dartboard, which is a project or list of tasks."
                },
                "type": {
                  "type": "string",
                  "description": "The title of the type of the task."
                },
                "status": {
                  "type": "string",
                  "description": "The status from the list of available statuses."
                },
                "description": {
                  "type": "string",
                  "description": "A longer description of the task, which can include markdown formatting."
                },
                "assignees": {
                  "type": [
                    "array",
                    "null"
                  ],
                  "items": {
                    "type": "string"
                  },
                  "description": "Names or emails replacing all assignees for a workspace allowing multiple assignees. An empty array clears them."
                },
                "assignee": {
                  "type": [
                    "string",
                    "null"
                  ],
                  "description": "The assignee's name or email for a workspace using a single assignee. Null clears the assignee."
                },
                "reviewers": {
                  "type": [
                    "array",
                    "null"
                  ],
                  "items": {
                    "type": "string"
                  },
                  "description": "Names or emails replacing all reviewers for a workspace allowing multiple reviewers. An empty array clears them."
                },
                "reviewer": {
                  "type": [
                    "string",
                    "null"
                  ],
                  "description": "The reviewer's name or email for a workspace using a single reviewer. Null clears the reviewer."
                },
                "tags": {
                  "type": "array",
                  "items": {
                    "type": "string"
                  },
                  "description": "Tag titles replacing all task tags. An empty array clears them. Unknown titles create new tags; use / for nested tags."
                },
                "priority": {
                  "oneOf": [
                    {
                      "enum": [
                        "Critical",
                        "High",
                        "Medium",
                        "Low"
                      ],
                      "type": "string",
                      "description": "* `Critical` - CRITICAL\n* `High` - HIGH\n* `Medium` - MEDIUM\n* `Low` - LOW",
                      "x-enum-varnames": [
                        "CRITICAL",
                        "HIGH",
                        "MEDIUM",
                        "LOW"
                      ]
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "description": "The priority, which is a string that can be one of the specified options. This is used to sort tasks and determine which tasks should be done first."
                },
                "startAt": {
                  "type": [
                    "string",
                    "null"
                  ],
                  "description": "The start date, which is a date that the task should be started by in ISO format, like YYYY-MM-DD."
                },
                "dueAt": {
                  "type": [
                    "string",
                    "null"
                  ],
                  "description": "The due date, which is a date that the task should be completed by in ISO format, like YYYY-MM-DD."
                },
                "size": {
                  "type": [
                    "string",
                    "integer",
                    "null"
                  ],
                  "description": "The size, which represents the amount of work that needs to be done. This is used to determine how long the task will take to complete."
                },
                "customProperties": {
                  "type": [
                    "object",
                    "null"
                  ],
                  "description": "Custom properties as a dict mapping the exact, case-sensitive name values from workspace config customProperties to values. These are generated keys, not display titles or IDs (e.g., {\"customCheckboxProperty\": true, \"customTextProperty\": \"Some text\"}). Only supplied properties change. Null clears all custom properties; a null entry clears that property when its type accepts null.",
                  "properties": {},
                  "additionalProperties": {
                    "anyOf": [
                      {
                        "title": "CustomPropertyCheckbox",
                        "type": "boolean"
                      },
                      {
                        "title": "CustomPropertyDatesRange",
                        "type": [
                          "array",
                          "null"
                        ],
                        "items": {
                          "type": [
                            "string",
                            "null"
                          ]
                        }
                      },
                      {
                        "title": "CustomPropertyDatesSingle",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      {
                        "title": "CustomPropertyMultiselect",
                        "type": "array",
                        "items": {
                          "type": "string"
                        }
                      },
                      {
                        "title": "CustomPropertyNumberWithIntegerFormat",
                        "type": [
                          "integer",
                          "null"
                        ]
                      },
                      {
                        "title": "CustomPropertyNumberWithPercentageFormat",
                        "type": [
                          "integer",
                          "null"
                        ]
                      },
                      {
                        "title": "CustomPropertyNumberWithDollarsFormat",
                        "type": [
                          "number",
                          "null"
                        ]
                      },
                      {
                        "title": "CustomPropertySelect",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      {
                        "title": "CustomPropertyStatus",
                        "type": "string"
                      },
                      {
                        "title": "CustomPropertyText",
                        "type": "string"
                      },
                      {
                        "title": "CustomPropertyUserMultiple",
                        "type": "array",
                        "items": {
                          "type": "string"
                        }
                      },
                      {
                        "title": "CustomPropertyUserSingle",
                        "type": [
                          "string",
                          "null"
                        ]
                      }
                    ]
                  },
                  "example": {
                    "customCheckboxProperty": true,
                    "customDatesProperty": "2025-05-10",
                    "customDatesPropertyWithRange": [
                      "2025-05-01",
                      "2025-05-30"
                    ],
                    "customMultiselectProperty": [
                      "frontend",
                      "bug"
                    ],
                    "customNumberPropertyWithIntegerFormat": 5,
                    "customNumberPropertyWithPercentageFormat": 75,
                    "customNumberPropertyWithDollarsFormat": 1500.5,
                    "customSelectProperty": "In Progress",
                    "customStatusProperty": "Blocked",
                    "customTextProperty": "This task requires additional review from the design team",
                    "customUserProperty": "john.doe@example.com",
                    "customMultipleUserProperty": [
                      "john.doe@example.com",
                      "Alice Smith"
                    ]
                  }
                },
                "taskRelationships": {
                  "oneOf": [
                    {
                      "type": "object",
                      "properties": {
                        "subtaskIds": {
                          "type": "array",
                          "items": {
                            "type": "string",
                            "pattern": "^[a-zA-Z0-9]{12}$",
                            "description": "The universal, unique ID of the task."
                          },
                          "description": "Array of task IDs that are subtasks of this task. These tasks have a parent-child relationship where this task is the parent. Subtasks inherit context from their parent and are typically smaller units of work."
                        },
                        "blockerIds": {
                          "type": "array",
                          "items": {
                            "type": "string",
                            "pattern": "^[a-zA-Z0-9]{12}$",
                            "description": "The universal, unique ID of the task."
                          },
                          "description": "Array of task IDs that block this task from being completed. These are dependencies that must be resolved/completed before this task can proceed. The blocking tasks have priority over this task."
                        },
                        "blockingIds": {
                          "type": "array",
                          "items": {
                            "type": "string",
                            "pattern": "^[a-zA-Z0-9]{12}$",
                            "description": "The universal, unique ID of the task."
                          },
                          "description": "Array of task IDs that are blocked by this task. This task must be completed before the blocked tasks can proceed. This task is a dependency for the blocked tasks and has priority over them."
                        },
                        "duplicateIds": {
                          "type": "array",
                          "items": {
                            "type": "string",
                            "pattern": "^[a-zA-Z0-9]{12}$",
                            "description": "The universal, unique ID of the task."
                          },
                          "description": "Array of task IDs that are duplicates of this task. These represent the same work item and should typically be consolidated to avoid duplicate effort. Only one of the duplicates should be completed."
                        },
                        "relatedIds": {
                          "type": "array",
                          "items": {
                            "type": "string",
                            "pattern": "^[a-zA-Z0-9]{12}$",
                            "description": "The universal, unique ID of the task."
                          },
                          "description": "Array of task IDs that are related to this task. These tasks have some contextual relationship but no direct dependency. They may share similar goals, components, or be part of the same feature/epic."
                        }
                      },
                      "additionalProperties": false,
                      "nullable": true,
                      "example": {
                        "subtaskIds": [
                          "abcdefghijk1",
                          "abcdefghijk2"
                        ],
                        "blockerIds": [
                          "abcdefghijk3"
                        ],
                        "blockingIds": [
                          "abcdefghijk4"
                        ],
                        "duplicateIds": [
                          "abcdefghijk5"
                        ],
                        "relatedIds": [
                          "abcdefghijk6",
                          "abcdefghijk7"
                        ]
                      }
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "description": "The relationships associated with the task."
                }
              },
              "required": [
                "id"
              ],
              "description": "The task ID and fields to change. Use the same ID as the path id; omitted fields keep their current values."
            }
          },
          "required": [
            "item"
          ]
        }
      },
      "required": [
        "id",
        "requestBody"
      ]
    },
    execute: (args) => __request(config, {
      method: 'PUT',
      url: '/api/v0/ai/tasks/{id}',
      path: { 'id': args["id"] },
      body: args['requestBody'],
    }),
  },
  {
    name: "update_task_description",
    description: "Apply targeted text updates to a task's description; use instead of updateTask when only the description changes. Each update is one of: \"replace\" (swap oldText for newText), \"insert_before\" / \"insert_after\" (insert newText relative to anchorText), or \"delete\" (remove oldText), applied in order and atomically. When occurrence is omitted, the target text must be unique; otherwise specify occurrence (1-indexed). Preferred over a full update for long content: fewer tokens, and no risk of rewriting unrelated text.",
    inputSchema: {
      "type": "object",
      "properties": {
        "id": {
          "type": "string",
          "pattern": "^[a-zA-Z0-9]{12}$",
          "description": "The task's 12-character alphanumeric Dart ID; accepts an ID, not a title or URL."
        },
        "requestBody": {
          "type": "object",
          "description": "Payload for applying a list of targeted text updates to a task's description.",
          "properties": {
            "updates": {
              "type": "array",
              "items": {
                "type": "object",
                "properties": {
                  "type": {
                    "enum": [
                      "replace",
                      "insert_before",
                      "insert_after",
                      "delete"
                    ],
                    "type": "string",
                    "description": "The kind of update. \"replace\" swaps oldText for newText. \"insert_before\" and \"insert_after\" insert newText immediately before or after anchorText. \"delete\" removes oldText.",
                    "x-enum-varnames": [
                      "REPLACE",
                      "INSERT_BEFORE",
                      "INSERT_AFTER",
                      "DELETE"
                    ]
                  },
                  "oldText": {
                    "type": "string",
                    "description": "The exact text to find. Required for \"replace\" and \"delete\"."
                  },
                  "anchorText": {
                    "type": "string",
                    "description": "The exact text to find as an insertion anchor. Required for \"insert_before\" and \"insert_after\"."
                  },
                  "newText": {
                    "type": "string",
                    "description": "The text to insert. Required for \"replace\", \"insert_before\", and \"insert_after\"."
                  },
                  "occurrence": {
                    "type": "integer",
                    "minimum": 1,
                    "description": "Which occurrence of oldText or anchorText to act on, 1-indexed. When omitted, the search text must be unique in the current content."
                  }
                },
                "required": [
                  "type"
                ]
              },
              "description": "An ordered list of text updates to apply. Each one operates on the result of the previous one. Applied atomically \u2014 if any update fails, none are persisted.",
              "minItems": 1
            }
          },
          "required": [
            "updates"
          ]
        }
      },
      "required": [
        "id",
        "requestBody"
      ]
    },
    execute: (args) => __request(config, {
      method: 'POST',
      url: '/api/v0/ai/tasks/{id}/update-description',
      path: { 'id': args["id"] },
      body: args['requestBody'],
    }),
  },
]
